import { useMemo } from 'react';
import { createFileRoute } from '@tanstack/react-router';
import { useIxcGenerateV1MonthlyStatementsJobQueryServiceMonthlyStatementsJobGet } from '@interxfi/api-client/generated/hooks/ixc-generate-v1-monthly-statements-job-query-service-monthly-statements-job-get';
import {
  EntityActionMenu,
  type EntityActionMenuConfig,
  type EntityActionMenuItem,
} from '@interxfi/app-components/components/entity-action-menu/entity-action-menu';
import { PageHeader } from '@interxfi/app-components/components/layout/page-header/page-header';
import {
  DEFAULT_PAGE_LAYOUT_GAP,
  DEFAULT_PAGE_LAYOUT_MAX_WIDTH_CONTAINER_SIZE,
  PageLayout,
} from '@interxfi/app-components/components/layout/page-layout/page-layout';
import { AppReadonlyListStack } from '@interxfi/app-components/components/readonly-list/app-readonly-list-stack';
import { BoxedAppReadonlyList } from '@interxfi/app-components/components/readonly-list/boxed-app-readonly-list';
import { type AggregateBreadcrumb, buildBreadcrumb } from '@interxfi/app-components/components/router-breadcrumbs/router-breadcrumbs';
import { HorizontalStack } from '@interxfi/design-system/components/alignment/horizontal-stack';
import { VerticalStack } from '@interxfi/design-system/components/alignment/vertical-stack';
import { CopyButton } from '@interxfi/design-system/components/copy-button/copy-button';
import { LAYOUT_ZERO_CONTAINER_PADDING } from '@interxfi/design-system/components/layout-container/layout-container';
import {
  DEFAULT_WRAPPING_LAYOUT_COLUMN,
  WrappingLayoutContainer,
} from '@interxfi/design-system/components/layout-container/wrapping/wrapping-layout-container';
import type { ReadonlyListGroup } from '@interxfi/design-system/components/list/readonly/stacked/readonly-list-stack';
import type { CustomTableHeaderProps } from '@interxfi/design-system/components/table';
import { Typography } from '@interxfi/design-system/components/typography';
import RepeatIcon from '@interxfi/design-system/icons/react/icon-repeat';
import type { IxcGenerateV1MonthlyStatementsJobState } from '@interxfi/types/generated/api';
import { TableSectionHeader } from '../../../../../../../../../../../components/table/table-section-header';
import {
  DocgenMonthlyStatementJobEventDrawer,
  DocgenMonthlyStatementJobEventSummary,
} from '../../../../../../../../../../../features/generate/monthly-statement/job/docgen-monthly-statement-job-event';
import { getDocgenMonthlyStatementJobStateListItems } from '../../../../../../../../../../../features/generate/monthly-statement/job/docgen-monthly-statement-job-list-items';
import { DocgenMonthlyStatementJobRetryConfirmDialog } from '../../../../../../../../../../../features/generate/monthly-statement/job/docgen-monthly-statement-job-retry-confirm-dialog';
import { getDocgenMonthlyStatementJobDetailPath } from '../../../../../../../../../../../features/generate/monthly-statement/job/docgen-monthly-statement-job-routing';
import { MonthlyStatementJobStatusPill } from '../../../../../../../../../../../features/generate/monthly-statement/job/monthly-statement-job-status-pill';
import { DocgenMonthlyStatementTasksTable } from '../../../../../../../../../../../features/generate/monthly-statement/task/docgen-monthly-statement-tasks-table';
import { buildDefaultErrorMessageGetter, useErrorHandler } from '../../../../../../../../../../../util/error-handler';
import { getPageHead } from '../../../../../../../../../../../util/routing/meta';
import { useRollupViewContext } from '../../../../../../../../../../../util/view-context';

export const Route = createFileRoute(
  '/_app/_clearing-core/_clearing-required/clearing/operations/_operations/generate/monthly-statement/job/_job/$jobId',
)({
  loader: ({ params }) =>
    ({
      breadcrumb: buildBreadcrumb({
        id: `monthly-statement-job-${params.jobId}`,
        label: 'Job',
        to: getDocgenMonthlyStatementJobDetailPath(params.jobId),
        suffix: <CopyButton content={params.jobId} label="Copy Job ID" tooltip={{ copy: params.jobId, copied: 'Copied Job ID' }} />,
      }),
    }) satisfies AggregateBreadcrumb,
  head: () => getPageHead('Monthly Statement Job'),
  component: DocgenMonthlyStatementJobDetail,
});

function getListItems(data: IxcGenerateV1MonthlyStatementsJobState | undefined) {
  const items = getDocgenMonthlyStatementJobStateListItems(data);

  return {
    general: {
      left: [
        {
          id: 'main',
          items: [
            items.data.spec.startDate,
            items.data.spec.endDate,
            items.data.spec.revision,
            ...(items.data.spec.retryOfJobId ? [items.data.spec.retryOfJobId] : []),
          ],
        },
        {
          id: 'metrics',
          title: 'Metrics',
          items: [
            items.data.metrics.total,
            items.data.metrics.succeeded,
            items.data.metrics.errored,
            items.data.metrics.failed,
            items.data.metrics.pending,
          ],
        },
      ] as const satisfies ReadonlyListGroup[],
      right: [items.metadata.createdAt, items.metadata.updatedAt, items.jobId],
    },
  };
}

function getMenuItems(jobId: string): EntityActionMenuConfig {
  return {
    eventDrawer: {
      type: 'drawer',
      drawer: {
        Component: DocgenMonthlyStatementJobEventDrawer,
        props: { request: { jobId } },
      },
    },
    actions: [
      {
        id: 'retry',
        icon: <RepeatIcon aria-hidden />,
        label: 'Rerun Job',
        interaction: {
          type: 'modal',
          modal: {
            Component: DocgenMonthlyStatementJobRetryConfirmDialog,
            props: { jobId },
          },
        },
        requiresWrite: true,
      } satisfies EntityActionMenuItem,
    ],
  };
}

function TasksTableHeader(props: CustomTableHeaderProps) {
  return <TableSectionHeader {...props} title="Tasks" />;
}

const errorMessageGetter = buildDefaultErrorMessageGetter('Failed to load monthly statement job', 'view this monthly statement job');

function DocgenMonthlyStatementJobDetail() {
  const { jobId } = Route.useParams();
  const viewContext = useRollupViewContext();
  const request = { jobId } as const;
  const { data, error, isPending } = useIxcGenerateV1MonthlyStatementsJobQueryServiceMonthlyStatementsJobGet(request);
  useErrorHandler(error, errorMessageGetter);
  const showPending = Boolean(isPending || error);
  const listItems = useMemo(() => getListItems(data?.monthlyStatementsJob), [data?.monthlyStatementsJob]);
  const menuItems = useMemo(() => getMenuItems(jobId), [jobId]);

  return (
    <PageLayout maxWidthContainer={DEFAULT_PAGE_LAYOUT_MAX_WIDTH_CONTAINER_SIZE}>
      <PageLayout.Section noBorder>
        <PageHeader>
          <HorizontalStack gap={2} wrap="nowrap">
            <MonthlyStatementJobStatusPill isPending={showPending} size="large" status={data?.monthlyStatementsJob?.status} />
          </HorizontalStack>

          <EntityActionMenu {...menuItems} isPending={showPending} />
        </PageHeader>
      </PageLayout.Section>

      <PageLayout.Section>
        <VerticalStack gap={4}>
          <Typography.Level3>Events</Typography.Level3>

          <DocgenMonthlyStatementJobEventSummary gap={DEFAULT_PAGE_LAYOUT_GAP} padding={LAYOUT_ZERO_CONTAINER_PADDING} request={request} />
        </VerticalStack>
      </PageLayout.Section>

      <PageLayout.Section>
        <WrappingLayoutContainer
          gap={DEFAULT_PAGE_LAYOUT_GAP}
          left={{
            ...DEFAULT_WRAPPING_LAYOUT_COLUMN,
            render: <AppReadonlyListStack groups={listItems.general.left} isPending={showPending} titleGap={4} />,
          }}
          padding={LAYOUT_ZERO_CONTAINER_PADDING}
          right={{
            ...DEFAULT_WRAPPING_LAYOUT_COLUMN,
            render: <BoxedAppReadonlyList isPending={showPending} items={listItems.general.right} />,
          }}
          wrap="forward"
          wrapGap={7}
        />
      </PageLayout.Section>

      <PageLayout.Section>
        <DocgenMonthlyStatementTasksTable HeaderActions={TasksTableHeader} jobId={jobId} view={viewContext} />
      </PageLayout.Section>
    </PageLayout>
  );
}
